import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TenderDetailsModal = ({ isOpen, onClose, tender }) => {
  const [showPreview, setShowPreview] = useState(false);

  if (!isOpen || !tender) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'closing soon':
        return 'bg-orange-100 text-orange-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining(tender?.deadline);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Briefcase" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{tender?.title}</h2>
              <p className="text-sm text-muted-foreground">Tender ID: {tender?.tenderId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tender?.status)}`}>
              {tender?.status}
            </span>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Description */}
          <div>
            <h3 className="font-semibold text-foreground mb-2">Description</h3>
            <p className="text-muted-foreground">{tender?.description}</p>
          </div>

          {/* Tender Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-foreground mb-1">Published Date</h4>
              <p className="text-muted-foreground">{formatDate(tender?.publishedDate)}</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Deadline</h4>
              <p className="text-muted-foreground">{formatDate(tender?.deadline)}</p>
            </div>
            {daysRemaining > 0 && (
              <div className="md:col-span-2">
                <h4 className="font-medium text-foreground mb-1">Time Remaining</h4>
                <p className={`text-sm font-medium ${daysRemaining <= 7 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                  {daysRemaining} days remaining
                </p>
              </div>
            )}
          </div>

          {/* Tender Documents */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">Tender Documents</h3>
              <Button
                variant="outline"
                size="sm"
                iconName={showPreview ? "EyeOff" : "Eye"}
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? "Hide Preview" : "Preview Document"}
              </Button>
            </div>
            <div className="bg-muted/30 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
                    <Icon name="FileText" size={16} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Tender Document</p>
                    <p className="text-sm text-muted-foreground">PDF Document</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = "/assets/brochure.pdf";
                    link.download = `Tender_${tender?.tenderId}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download
                </Button>
              </div>
              {showPreview && (
                <div className="mt-4">
                  <iframe
                    src="/assets/brochure.pdf"
                    className="w-full h-96 border-0 rounded"
                    title={`Preview of ${tender?.title}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="font-semibold text-foreground mb-3">Contact Information</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={14} />
                <span>info@karumotti.ac.ke</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={14} />
                <span>+254(0)705636443</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} />
                <span>Mon-Fri: 8:00 AM - 5:00 PM</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="default"
            iconName="Download"
            onClick={() => {
              const link = document.createElement('a');
              link.href = "/assets/brochure.pdf";
              link.download = `Tender_${tender?.tenderId}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download Tender Document
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenderDetailsModal;
